import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { RouterLink } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { OrderList, OrderStatus } from '../../../models/order.model';
import { OrderService } from '../../../services/order.service';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagStatusComponent } from '../../../../shared/components/tag-status/tag-status.component';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-order-list',
  imports: [
    FormsModule,
    TableModule,
    ButtonModule,
    ToolbarModule,
    BreadcrumbModule,
    ProgressSpinnerModule,
    ToastModule,
    RouterLink,
    DatePipe,
    InputTextModule,
    SelectModule,
    TagStatusComponent,
    CurrencyPipe,
  ],
  templateUrl: './order-list.component.html',
})
export class OrderListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private orderService = inject(OrderService);
  private messageService = inject(MessageService);
  paginationService = inject(PaginationService);

  orders = signal<OrderList[]>([]);

  pageNumber = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);
  totalPages = signal<number>(0);

  loading = signal(false);

  numberFilter?: string;
  fullNameFilter?: string;
  productNameFilter?: string;
  statusFilter?: OrderStatus;

  orderStatusOptions = this.orderService.getOrderStatusOptions();

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  getStatusTag(status: OrderStatus): string {
    return this.orderService.getStatusTag(status);
  }

  getStatusDescription(status: OrderStatus): string {
    return this.orderService.getStatusDescription(status);
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize(),
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.orderService
      .getAll(
        this.pageNumber(),
        this.pageSize(),
        this.numberFilter,
        this.fullNameFilter,
        this.productNameFilter,
        this.statusFilter,
      )
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.orders.set(page.items as OrderList[]);
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar os pedidos',
              life: 3000,
            });
          }
        },
      });
  }
}
